import { useState, useEffect } from 'react';
import { API_URL, fetchProducts, type ApiProduct } from '../lib/api';

export interface Product {
  id: string;
  name: string;
  slug: string;
  color: string;
  colorHex: string;
  texture: string;
  pricePerM2: number;
  /** v5 blok 4: levertermijn in weken (uit de API; null = onbekend). */
  deliveryWeeks?: number | null;
  /** v5 blok 9: technische fiche van de fabrikant (absolute URL) of null. */
  ficheUrl?: string | null;
  /** v5 blok 9: productnummer (P-1001 …). */
  serialNumber?: string | null;
  wholesalePrice: Record<number, number>;
  supplierCode: string;
  supplier: string;
  images: string[];
  description: string;
  dimensions: string;
  weight: string;
  category: string;
  // ─── Optional discount badges (set when API returns them) ───────────────
  show_discount_badge?: number;
  discount_pct?: number;
  discount_label?: string | null;
  liquidation_active?: number;
  liquidation_pct?: number;
  liquidation_label?: string | null;
  liquidation_start?: number | null;
  liquidation_end?: number | null;
}

export interface InspirationProject {
  id: string;
  title: string;
  style: 'modern' | 'landelijk' | 'industrieel' | 'klassiek';
  image: string;
  /** Val terug op de steenfoto zolang er geen echte huisfoto in /inspiration staat. */
  fallbackImage?: string;
  products: string[];
  location: string;
  architect?: string;
  description: string;
  /** v5 blok 3: gebruikt voegtype (kleur + breedte) — vermeld bij elk project. */
  grout?: string;
}

const IMG = '/products';

export const PRODUCTS: Product[] = [
  // --- LINEA / LANGWERPIGE STRIPS (modern, strak) ---
  {
    id: 'p1', name: 'Linea Taupe', slug: 'linea-taupe', color: 'Taupe', colorHex: '#b0a090', texture: 'Glad',
    pricePerM2: 54, wholesalePrice: { 1: 39, 2: 35, 3: 31 }, supplierCode: 'GS-LT-001', supplier: 'Fabrikant Polen',
    images: [`${IMG}/linea-taupe.jpg`],
    description: 'Strakke langwerpige strip in warm taupe. Perfect voor moderne gevelbekleding met een zachte, neutrale uitstraling.',
    dimensions: '468 x 38 x 15 mm', weight: '3.8 kg/m²', category: 'modern'
  },
  {
    id: 'p2', name: 'Linea Antraciet', slug: 'linea-antraciet', color: 'Grijs', colorHex: '#5a5a5a', texture: 'Glad',
    pricePerM2: 56, wholesalePrice: { 1: 40, 2: 36, 3: 32 }, supplierCode: 'GS-LA-002', supplier: 'Fabrikant Polen',
    images: [`${IMG}/linea-antraciet.jpg`],
    description: 'Donkergrijze langwerpige strip met subtiele textuurvariatie. Geeft een stoere, hedendaagse look.',
    dimensions: '468 x 38 x 15 mm', weight: '3.9 kg/m²', category: 'modern'
  },
  {
    id: 'p3', name: 'Linea Wit', slug: 'linea-wit', color: 'Wit', colorHex: '#e8e4de', texture: 'Glad',
    pricePerM2: 52, wholesalePrice: { 1: 37, 2: 33, 3: 29 }, supplierCode: 'GS-LW-003', supplier: 'Fabrikant Polen',
    images: [`${IMG}/linea-wit.jpg`],
    description: 'Witte langwerpige strip met licht gestructureerd oppervlak. Tijdloos en elegant.',
    dimensions: '468 x 38 x 15 mm', weight: '3.7 kg/m²', category: 'modern'
  },
  {
    id: 'p4', name: 'Linea Lichtgrijs', slug: 'linea-lichtgrijs', color: 'Grijs', colorHex: '#b5b5b5', texture: 'Glad',
    pricePerM2: 53, wholesalePrice: { 1: 38, 2: 34, 3: 30 }, supplierCode: 'GS-LLG-004', supplier: 'Fabrikant Polen',
    images: [`${IMG}/linea-lichtgrijs.jpg`],
    description: 'Lichtgrijze langwerpige strip. Subtiel en verfijnd voor strakke architecturale projecten.',
    dimensions: '468 x 38 x 15 mm', weight: '3.8 kg/m²', category: 'modern'
  },
  {
    id: 'p5', name: 'Linea Blauwgrijs', slug: 'linea-blauwgrijs', color: 'Grijs', colorHex: '#6b7d8a', texture: 'Glad',
    pricePerM2: 55, wholesalePrice: { 1: 40, 2: 36, 3: 32 }, supplierCode: 'GS-LBG-005', supplier: 'Fabrikant Spanje',
    images: [`${IMG}/linea-blauwgrijs.jpg`],
    description: 'Blauwgrijze langwerpige strip met koele ondertoon. Uniek kleuraccent voor moderne gevels.',
    dimensions: '468 x 38 x 15 mm', weight: '3.9 kg/m²', category: 'modern'
  },
  {
    id: 'p6', name: 'Linea Zwart', slug: 'linea-zwart', color: 'Zwart', colorHex: '#2d2d2d', texture: 'Glad',
    pricePerM2: 58, wholesalePrice: { 1: 42, 2: 37, 3: 33 }, supplierCode: 'GS-LZ-006', supplier: 'Fabrikant Polen',
    images: [`${IMG}/linea-zwart.jpg`],
    description: 'Diepzwarte langwerpige strip voor een krachtig, exclusief gevelbeeld. Maximum contrast.',
    dimensions: '468 x 38 x 15 mm', weight: '4.0 kg/m²', category: 'modern'
  },

  // --- KLASSIEKE BAKSTEENSTRIPS ---
  {
    id: 'p7', name: 'Cottage Ivoor', slug: 'cottage-ivoor', color: 'Creme', colorHex: '#d9ccb5', texture: 'Ruw',
    pricePerM2: 46, wholesalePrice: { 1: 33, 2: 29, 3: 25 }, supplierCode: 'GS-CI-007', supplier: 'Fabrikant Polen',
    images: [`${IMG}/cottage-ivoor.jpg`],
    description: 'Landelijke ivoorkleurige strip met grijze nuances en ruw oppervlak. Authentiek en warm.',
    dimensions: '240 x 71 x 15 mm', weight: '4.2 kg/m²', category: 'landelijk'
  },
  {
    id: 'p8', name: 'Arctic Wit', slug: 'arctic-wit', color: 'Wit', colorHex: '#f0ece6', texture: 'Ruw',
    pricePerM2: 48, wholesalePrice: { 1: 34, 2: 30, 3: 26 }, supplierCode: 'GS-AW-008', supplier: 'Fabrikant Polen',
    images: [`${IMG}/arctic-wit.jpg`],
    description: 'Helder witte baksteenstrip met ruw karakter. Scandinavisch geïnspireerd, tijdloos strak.',
    dimensions: '240 x 71 x 15 mm', weight: '4.0 kg/m²', category: 'modern'
  },
  {
    id: 'p9', name: 'Toscana Rustiek', slug: 'toscana-rustiek', color: 'Rood', colorHex: '#b87340', texture: 'Ruw',
    pricePerM2: 47, wholesalePrice: { 1: 34, 2: 30, 3: 26 }, supplierCode: 'GS-TR-009', supplier: 'Fabrikant Spanje',
    images: [`${IMG}/toscana-rustiek.jpg`],
    description: 'Warme terracotta langwerpige strip met rustiek oppervlak. Mediterraans karakter.',
    dimensions: '400 x 50 x 15 mm', weight: '4.1 kg/m²', category: 'landelijk'
  },
  {
    id: 'p10', name: 'Oud-Hollands Bont', slug: 'oud-hollands-bont', color: 'Bont', colorHex: '#a5653a', texture: 'Ruw',
    pricePerM2: 44, wholesalePrice: { 1: 32, 2: 28, 3: 24 }, supplierCode: 'GS-OHB-010', supplier: 'Fabrikant Polen',
    images: [`${IMG}/oud-hollands-bont.jpg`],
    description: 'Veelkleurige baksteenstrip met authentieke stempels. Gerecycled karakter, elk stuk uniek.',
    dimensions: '240 x 71 x 15 mm', weight: '4.3 kg/m²', category: 'klassiek'
  },
  {
    id: 'p11', name: 'Roma Classico', slug: 'roma-classico', color: 'Rood', colorHex: '#b58060', texture: 'Ruw',
    pricePerM2: 42, wholesalePrice: { 1: 30, 2: 26, 3: 23 }, supplierCode: 'GS-RC-011', supplier: 'Fabrikant Polen',
    images: [`${IMG}/roma-classico.jpg`],
    description: 'Zachte roze-rode baksteenstrip met verweerde uitstraling. Klassiek Belgisch metselwerk.',
    dimensions: '240 x 71 x 15 mm', weight: '4.2 kg/m²', category: 'klassiek'
  },
  {
    id: 'p12', name: 'Carbon Noir', slug: 'carbon-noir', color: 'Zwart', colorHex: '#3a3a3a', texture: 'Ruw',
    pricePerM2: 55, wholesalePrice: { 1: 40, 2: 36, 3: 32 }, supplierCode: 'GS-CN-012', supplier: 'Fabrikant Spanje',
    images: [`${IMG}/carbon-noir.jpg`],
    description: 'Donkere antraciet-zwarte langwerpige strip. Industrieel en krachtig.',
    dimensions: '400 x 50 x 15 mm', weight: '4.4 kg/m²', category: 'industrieel'
  },
  {
    id: 'p13', name: 'Terracotta Warm', slug: 'terracotta-warm', color: 'Rood', colorHex: '#c08050', texture: 'Ruw',
    pricePerM2: 43, wholesalePrice: { 1: 31, 2: 27, 3: 24 }, supplierCode: 'GS-TW-013', supplier: 'Fabrikant Spanje',
    images: [`${IMG}/terracotta-warm.jpg`],
    description: 'Warme terracotta baksteenstrip met zachte kleurvariatie. Mediterrane charme.',
    dimensions: '240 x 71 x 15 mm', weight: '4.1 kg/m²', category: 'landelijk'
  },
  {
    id: 'p14', name: 'Nordic Wit Lang', slug: 'nordic-wit-lang', color: 'Wit', colorHex: '#e5e0d8', texture: 'Ruw',
    pricePerM2: 51, wholesalePrice: { 1: 37, 2: 33, 3: 29 }, supplierCode: 'GS-NWL-014', supplier: 'Fabrikant Polen',
    images: [`${IMG}/nordic-wit-lang.jpg`],
    description: 'Witte langwerpige strip met ruwe textuur. Scandinavische stijl, modern en licht.',
    dimensions: '400 x 50 x 15 mm', weight: '3.9 kg/m²', category: 'modern'
  },
  {
    id: 'p15', name: 'Kempisch Rood Bont', slug: 'kempisch-rood-bont', color: 'Bont', colorHex: '#a05030', texture: 'Ruw',
    pricePerM2: 44, wholesalePrice: { 1: 32, 2: 28, 3: 24 }, supplierCode: 'GS-KRB-015', supplier: 'Fabrikant Polen',
    images: [`${IMG}/kempisch-rood-bont.jpg`],
    description: 'Kempische bonte baksteenstrip met rood-bruine schakeringen. Authentiek Vlaams.',
    dimensions: '240 x 71 x 15 mm', weight: '4.3 kg/m²', category: 'klassiek'
  },
  {
    id: 'p16', name: 'Cottage Wit', slug: 'cottage-wit', color: 'Wit', colorHex: '#eae4dc', texture: 'Ruw',
    pricePerM2: 46, wholesalePrice: { 1: 33, 2: 29, 3: 25 }, supplierCode: 'GS-CW-016', supplier: 'Fabrikant Polen',
    images: [`${IMG}/cottage-wit.jpg`],
    description: 'Zachte witte baksteenstrip met landelijk karakter. Perfect voor cottage-stijl woningen.',
    dimensions: '240 x 71 x 15 mm', weight: '4.0 kg/m²', category: 'landelijk'
  },
  {
    id: 'p17', name: 'Amsterdam Grijs Bont', slug: 'amsterdam-grijs-bont', color: 'Grijs', colorHex: '#6a5a50', texture: 'Ruw',
    pricePerM2: 52, wholesalePrice: { 1: 37, 2: 33, 3: 29 }, supplierCode: 'GS-AGB-017', supplier: 'Fabrikant Spanje',
    images: [`${IMG}/amsterdam-grijs-bont.jpg`],
    description: 'Grijs-bruine langwerpige strip met genuanceerde kleurschakeringen. Stedelijk en stoer.',
    dimensions: '400 x 50 x 15 mm', weight: '4.2 kg/m²', category: 'industrieel'
  },
  {
    id: 'p18', name: 'Sienna Roest', slug: 'sienna-roest', color: 'Rood', colorHex: '#a85020', texture: 'Ruw',
    pricePerM2: 49, wholesalePrice: { 1: 35, 2: 31, 3: 27 }, supplierCode: 'GS-SR-018', supplier: 'Fabrikant Spanje',
    images: [`${IMG}/sienna-roest.jpg`],
    description: 'Warme roestbruine langwerpige strip. Rijke kleur met natuurlijke variatie.',
    dimensions: '400 x 50 x 15 mm', weight: '4.1 kg/m²', category: 'landelijk'
  },
  {
    id: 'p19', name: 'Graphite Dark', slug: 'graphite-dark', color: 'Grijs', colorHex: '#4a4a4a', texture: 'Ruw',
    pricePerM2: 54, wholesalePrice: { 1: 39, 2: 35, 3: 31 }, supplierCode: 'GS-GD-019', supplier: 'Fabrikant Polen',
    images: [`${IMG}/graphite-dark.jpg`],
    description: 'Donkergrijze baksteenstrip met diep karakter. Sterk en tijdloos.',
    dimensions: '240 x 71 x 15 mm', weight: '4.4 kg/m²', category: 'industrieel'
  },
  {
    id: 'p20', name: 'Bruges Bruin Genuanceerd', slug: 'bruges-bruin-genuanceerd', color: 'Bruin', colorHex: '#8a6a50', texture: 'Ruw',
    pricePerM2: 48, wholesalePrice: { 1: 34, 2: 30, 3: 26 }, supplierCode: 'GS-BBG-020', supplier: 'Fabrikant Polen',
    images: [`${IMG}/bruges-bruin-genuanceerd.jpg`],
    description: 'Warme bruine langwerpige strip met grijze nuances. Brugse elegantie.',
    dimensions: '400 x 50 x 15 mm', weight: '4.2 kg/m²', category: 'landelijk'
  },
  {
    id: 'p21', name: 'Provence Creme', slug: 'provence-creme', color: 'Creme', colorHex: '#d4c4b0', texture: 'Ruw',
    pricePerM2: 45, wholesalePrice: { 1: 32, 2: 28, 3: 25 }, supplierCode: 'GS-PC-021', supplier: 'Fabrikant Spanje',
    images: [`${IMG}/provence-creme.jpg`],
    description: 'Zachte cremekleurige baksteenstrip met Provençaalse warmte. Licht en elegant.',
    dimensions: '240 x 71 x 15 mm', weight: '4.0 kg/m²', category: 'landelijk'
  },
  {
    id: 'p22', name: 'Misty Grijs', slug: 'misty-grijs', color: 'Grijs', colorHex: '#9a9a9a', texture: 'Ruw',
    pricePerM2: 50, wholesalePrice: { 1: 36, 2: 32, 3: 28 }, supplierCode: 'GS-MG-022', supplier: 'Fabrikant Polen',
    images: [`${IMG}/misty-grijs.jpg`],
    description: 'Zacht grijs met subtiele variatie. Moderne uitstraling met een vleugje karakter.',
    dimensions: '240 x 71 x 15 mm', weight: '4.1 kg/m²', category: 'modern'
  },
  {
    id: 'p23', name: 'Classic Rood', slug: 'classic-rood', color: 'Rood', colorHex: '#b06040', texture: 'Ruw',
    pricePerM2: 41, wholesalePrice: { 1: 29, 2: 25, 3: 22 }, supplierCode: 'GS-CR-023', supplier: 'Fabrikant Polen',
    images: [`${IMG}/classic-rood.jpg`],
    description: 'Klassieke rode baksteenstrip. De tijdloze keuze voor traditionele Belgische gevels.',
    dimensions: '240 x 71 x 15 mm', weight: '4.2 kg/m²', category: 'klassiek'
  },
  {
    id: 'p24', name: 'Obsidian Zwart', slug: 'obsidian-zwart', color: 'Zwart', colorHex: '#252525', texture: 'Ruw',
    pricePerM2: 56, wholesalePrice: { 1: 40, 2: 36, 3: 32 }, supplierCode: 'GS-OZ-024', supplier: 'Fabrikant Spanje',
    images: [`${IMG}/obsidian-zwart.jpg`],
    description: 'Diepzwarte baksteenstrip met witte voeg. Dramatisch contrast, exclusief design.',
    dimensions: '240 x 71 x 15 mm', weight: '4.5 kg/m²', category: 'modern'
  },
  {
    id: 'p25', name: 'Sahara Zand', slug: 'sahara-zand', color: 'Geel', colorHex: '#c8b480', texture: 'Ruw',
    pricePerM2: 47, wholesalePrice: { 1: 34, 2: 30, 3: 26 }, supplierCode: 'GS-SZ-025', supplier: 'Fabrikant Spanje',
    images: [`${IMG}/sahara-zand.jpg`],
    description: 'Zandkleurige langwerpige strip met warme gele ondertoon. Licht en vriendelijk.',
    dimensions: '400 x 50 x 15 mm', weight: '3.9 kg/m²', category: 'klassiek'
  },
  {
    id: 'p26', name: 'Toscana Licht', slug: 'toscana-licht', color: 'Rood', colorHex: '#c49070', texture: 'Ruw',
    pricePerM2: 43, wholesalePrice: { 1: 31, 2: 27, 3: 24 }, supplierCode: 'GS-TL-026', supplier: 'Fabrikant Spanje',
    images: [`${IMG}/toscana-licht.jpg`],
    description: 'Lichte terracotta baksteenstrip. Zachte roze-rode tinten met Toscaans karakter.',
    dimensions: '240 x 71 x 15 mm', weight: '4.1 kg/m²', category: 'landelijk'
  },
  {
    id: 'p27', name: 'Vlaamse Bont', slug: 'vlaamse-bont', color: 'Bont', colorHex: '#9a6040', texture: 'Ruw',
    pricePerM2: 45, wholesalePrice: { 1: 32, 2: 28, 3: 25 }, supplierCode: 'GS-VB-027', supplier: 'Fabrikant Polen',
    images: [`${IMG}/vlaamse-bont.jpg`],
    description: 'Bonte baksteenstrip met rood-bruine kleurmix en donkere accenten. Echt Vlaams.',
    dimensions: '240 x 71 x 15 mm', weight: '4.3 kg/m²', category: 'klassiek'
  },
];

export const COLORS = ['Rood', 'Wit', 'Bruin', 'Zwart', 'Geel', 'Grijs', 'Taupe', 'Creme', 'Bont'];
export const TEXTURES = ['Ruw', 'Glad', 'Gesinterd', 'Gewassen'];

// ─── API-aware product hook ────────────────────────────────────────────────────

function apiToProduct(p: ApiProduct): Product {
  return {
    id: p.id,
    name: p.name,
    slug: p.slug,
    color: p.color,
    colorHex: p.color_hex,
    texture: p.texture,
    pricePerM2: p.locked_price ?? 0,
    deliveryWeeks: p.delivery_weeks ?? null,
    // Fiche-pad van de API is relatief (/api/uploads/r2/...) — absoluut maken
    // zodat window.open naar de Workers-API gaat, niet naar de Pages-origin.
    ficheUrl: p.fiche_url ? (p.fiche_url.startsWith('/') ? API_URL + p.fiche_url : p.fiche_url) : null,
    serialNumber: p.serial_number ?? null,
    wholesalePrice: {},
    supplierCode: p.supplier_code,
    supplier: p.supplier_id ?? '',
    images: p.primary_image ? [p.primary_image] : [`/products/${p.slug}.jpg`],
    description: p.description_nl,
    dimensions: p.dimensions,
    weight: p.weight,
    category: p.category,
    // Discount badge fields — pass through unchanged
    show_discount_badge: p.show_discount_badge,
    discount_pct: p.discount_pct,
    discount_label: p.discount_label ?? null,
    liquidation_active: p.liquidation_active,
    liquidation_pct: p.liquidation_pct,
    liquidation_label: p.liquidation_label ?? null,
    liquidation_start: p.liquidation_start ?? null,
    liquidation_end: p.liquidation_end ?? null,
  };
}

/**
 * Returns the live product list (from API) with graceful fallback to the
 * hardcoded PRODUCTS array when the API is unavailable.
 */
export function useProducts(): { products: Product[]; loading: boolean } {
  const [products, setProducts] = useState<Product[]>(PRODUCTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchProducts()
      .then(data => {
        if (cancelled) return;
        if (data && data.length > 0) {
          const merged = data.map(apiP => {
            const local = PRODUCTS.find(lp => lp.id === apiP.id || lp.slug === apiP.slug);
            const base = apiToProduct(apiP);
            if (local) {
              // Prijs komt uit de database (locked_price); hardcoded prijs is
              // alleen het vangnet als de API geen prijs meegeeft.
              if (!base.pricePerM2) base.pricePerM2 = local.pricePerM2;
              base.wholesalePrice = local.wholesalePrice;
              base.images = local.images;
            }
            return base;
          });
          setProducts(merged);
        }
      })
      .catch(() => { /* API down → keep hardcoded fallback */ })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  return { products, loading };
}

// ─── i18n helpers ─────────────────────────────────────────────────────────────

export function translateColor(color: string, t: (key: string) => string): string {
  return t(`data.color.${color}`);
}

export function translateTexture(texture: string, t: (key: string) => string): string {
  return t(`data.texture.${texture}`);
}

export function translateSupplier(supplier: string, t: (key: string) => string): string {
  const map: Record<string, string> = {
    'Fabrikant Polen': 'data.supplier.polen',
    'Fabrikant Spanje': 'data.supplier.spanje',
    'Fabrikant Italië': 'data.supplier.italie',
  };
  return t(map[supplier] || supplier);
}

export function translateProductDesc(productId: string, t: (key: string) => string): string {
  const key = `data.product.${productId}.desc`;
  const translated = t(key);
  if (translated === key) {
    const product = PRODUCTS.find(p => p.id === productId);
    return product?.description || '';
  }
  return translated;
}

export function translateInspTitle(inspId: string, t: (key: string) => string): string {
  const v = t(`data.insp.${inspId}.title`);
  if (!v.startsWith('data.insp.')) return v;
  // v5.28: nieuwe projecten hebben (nog) geen vertaalsleutel — val terug op
  // de eigen titel uit de projectdata.
  return INSPIRATION_PROJECTS.find(pr => pr.id === inspId)?.title ?? v;
}

export function translateInspDesc(inspId: string, t: (key: string) => string): string {
  const v = t(`data.insp.${inspId}.desc`);
  if (!v.startsWith('data.insp.')) return v;
  return INSPIRATION_PROJECTS.find(pr => pr.id === inspId)?.description ?? v;
}

export const INSPIRATION_PROJECTS: InspirationProject[] = [
  // v5.28 — uitgebreid naar 12 voorbeelden over alle vier de stijlen.
  // Beelden: best beschikbare muur-/steenfoto's; ECHTE projectfoto's van
  // afgewerkte huizen volgen van Filip (zie aanleverlijst).

  {
    id: 'i1', title: 'Villa Kalmthout', style: 'modern', image: `/inspiration/i1.jpg`, fallbackImage: `${IMG}/muur-closeup-1.jpg`,
    products: ['p3', 'p6'], location: 'Kalmthout, Antwerpen', grout: 'Grijs — 10 mm', architect: 'Studio Arcada',
    description: 'Strakke moderne villa met witte en zwarte gevelstrips in een bosrijke omgeving.'
  },
  {
    id: 'i2', title: 'Hoeve Restauratie Limburg', style: 'landelijk', image: `/inspiration/i2.jpg`, fallbackImage: `${IMG}/muur-closeup-2.jpg`,
    products: ['p11', 'p27'], location: 'Tongeren, Limburg', grout: 'Grijs — 10 mm', architect: 'Erfgoed Architecten',
    description: 'Authentieke renovatie van een Limburgse hoeve met rode en bonte baksteenstrips.'
  },
  {
    id: 'i3', title: 'Loft Gent Centrum', style: 'industrieel', image: `/inspiration/i3.jpg`, fallbackImage: `${IMG}/muur-closeup-3.jpg`,
    products: ['p19', 'p12'], location: 'Gent, Oost-Vlaanderen', grout: 'Grijs — 10 mm',
    description: 'Industrieel loft-project in hartje Gent met grijze en zwarte accenten.'
  },
  {
    id: 'i4', title: 'Villa Brugge', style: 'klassiek', image: `/inspiration/i4.jpg`, fallbackImage: `${IMG}/muur-closeup-4.jpg`,
    products: ['p20', 'p25'], location: 'Brugge, West-Vlaanderen', grout: 'Grijs — 10 mm', architect: 'Heritage Designs',
    description: 'Klassieke villa nabij Brugge met warme bruine en zandkleurige gevelstrips.'
  },
  {
    id: 'i5', title: 'Rijwoning Antwerpen-Zuid', style: 'modern', image: `/inspiration/i5.jpg`, fallbackImage: `${IMG}/muur-voorbeeld-1.jpg`,
    products: ['p2', 'p4'], location: 'Antwerpen', grout: 'Antraciet — 8 mm', architect: 'Atelier Noord',
    description: 'Gerenoveerde rijwoning met grijstinten en strakke voegen — stedelijk en tijdloos.'
  },
  {
    id: 'i6', title: 'Nieuwbouw BEN-woning Mechelen', style: 'modern', image: `/inspiration/i6.jpg`, fallbackImage: `${IMG}/muur-voorbeeld-2.jpg`,
    products: ['p1', 'p5'], location: 'Mechelen', grout: 'Grijs — 5 mm', architect: 'Bureau Vermeulen',
    description: 'Bijna-energieneutrale nieuwbouw met taupe en blauwgrijze accenten op de kopgevel.'
  },
  {
    id: 'i7', title: 'Fermette Pajottenland', style: 'landelijk', image: `/inspiration/i7.jpg`, fallbackImage: `${IMG}/muur-voorbeeld-3.jpg`,
    products: ['p7', 'p13'], location: 'Gooik, Vlaams-Brabant', grout: 'Zand — 12 mm', architect: 'Landhuis Architecten',
    description: 'Warme fermette met ivoor- en terracottastrips, aansluitend bij het glooiende landschap.'
  },
  {
    id: 'i8', title: 'Hoevewoning Westhoek', style: 'landelijk', image: `/inspiration/i8.jpg`, fallbackImage: `${IMG}/muur-voorbeeld-4.jpg`,
    products: ['p9', 'p18'], location: 'Poperinge, West-Vlaanderen', grout: 'Zand — 15 mm',
    description: 'Herbouwde hoevewoning met rustieke rode strips en brede zandkleurige voeg.'
  },
  {
    id: 'i9', title: 'Pakhuis Hasselt Kanaalkom', style: 'industrieel', image: `/inspiration/i9.jpg`, fallbackImage: `${IMG}/muur-voorbeeld-5.jpg`,
    products: ['p12', 'p17'], location: 'Hasselt, Limburg', grout: 'Antraciet — 10 mm', architect: 'KANAAL8',
    description: 'Herbestemd pakhuis aan het water met zwarte en grijsbonte stroken in wildverband.'
  },
  {
    id: 'i10', title: 'Kantoorloods Kortrijk', style: 'industrieel', image: `/inspiration/i10.jpg`, fallbackImage: `${IMG}/muur-voorbeeld-6.jpg`,
    products: ['p19', 'p6'], location: 'Kortrijk, West-Vlaanderen', grout: 'Grijs — 8 mm', architect: 'WERKPLEK bv',
    description: 'Loods omgebouwd tot kantoren; donkere gevelstrips geven het volume een industriële huid.'
  },
  {
    id: 'i11', title: 'Herenhuis Leuven Centrum', style: 'klassiek', image: `/inspiration/i11.jpg`, fallbackImage: `${IMG}/muur-closeup-4.jpg`,
    products: ['p10', 'p15'], location: 'Leuven, Vlaams-Brabant', grout: 'Wit — 12 mm', architect: 'Erfgoedstudio B',
    description: 'Herenhuis in het beschermde stadszicht, hersteld met bont metselwerk en witte voeg.'
  },
  {
    id: 'i12', title: 'Pastoriewoning Waasland', style: 'klassiek', image: `/inspiration/i12.jpg`, fallbackImage: `${IMG}/muur-closeup-5.jpg`,
    products: ['p23', 'p25'], location: 'Sint-Niklaas, Oost-Vlaanderen', grout: 'Zand — 10 mm',
    description: 'Klassieke pastoriewoning met warm rood en zandgeel — Vlaamse baksteentraditie in strips.'
  },

];

export const STYLES = ['modern', 'landelijk', 'industrieel', 'klassiek'] as const;
